import * as React from 'react';

import {
  applyAccent,
  applyTheme,
  readStoredAccent,
  readStoredChartStyle,
  readStoredTheme,
  storeChartStyle,
  type Accent,
  type ChartStyle,
  type Theme,
} from '@/lib/utils';

interface ThemeContextValue {
  theme: Theme;
  accent: Accent;
  chartStyle: ChartStyle;
  setTheme: (theme: Theme) => void;
  setAccent: (accent: Accent) => void;
  setChartStyle: (style: ChartStyle) => void;
  toggleTheme: () => void;
}

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  // The inline script in admin_spa.html already set the class before paint; this
  // just mirrors it into React state so the toggle has something to read.
  const [theme, setThemeState] = React.useState<Theme>(() => readStoredTheme());
  const [accent, setAccentState] = React.useState<Accent>(() => readStoredAccent());
  const [chartStyle, setChartStyleState] = React.useState<ChartStyle>(() => readStoredChartStyle());

  const setTheme = React.useCallback((next: Theme) => {
    applyTheme(next);
    setThemeState(next);
    // Mirrored to a cookie so the Django template can render the right class on
    // the very first byte and avoid a flash on hard navigation.
    document.cookie = `wer_admin_theme=${next}; path=/; max-age=31536000; samesite=lax`;
  }, []);

  const setAccent = React.useCallback((next: Accent) => {
    applyAccent(next);
    setAccentState(next);
    document.cookie = `wer_admin_accent=${next}; path=/; max-age=31536000; samesite=lax`;
  }, []);

  // Charts read this from React state, not the DOM, so no pre-paint script or
  // cookie mirror is needed — there is nothing to flash before hydration.
  const setChartStyle = React.useCallback((next: ChartStyle) => {
    storeChartStyle(next);
    setChartStyleState(next);
  }, []);

  React.useEffect(() => {
    applyTheme(theme);
    applyAccent(accent);
  }, [theme, accent]);

  const value = React.useMemo<ThemeContextValue>(
    () => ({
      theme,
      accent,
      chartStyle,
      setTheme,
      setAccent,
      setChartStyle,
      toggleTheme: () => setTheme(theme === 'dark' ? 'light' : 'dark'),
    }),
    [theme, accent, chartStyle, setTheme, setAccent, setChartStyle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = React.useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used inside ThemeProvider');
  return context;
}
